<template>
  <div>
    <div v-if="loginSecurity && loginSecurity.is_enabled">

      <v-form @submit.prevent="disableTFA"
              novalidate>
        <v-card>
          <v-card-title>
            Disable Two-Factor Authentication
          </v-card-title>
          <v-card-text>
            <h3>
              Disable Two-Factor Authentication
            </h3>
            <p>
              Disabling Two-Factor Authentication will no longer require the additional security step of entering a six-digit passcode from a two-factor authentication phone app before logging in to this account. You may re-enable it at any time. Please re-enter your password before disabling Two-Factor Authentication.
            </p>

            <v-text-field v-model="disableForm.code"
                          label="Authentication or Recovery Code"
                          outlined
                          placeholder="123456"
                          persistent-hint
                          hint="Enter the 6-digit code generated by your authentication app or one of your recovery codes to disable 2FA."
                          :rules="[rules.required]"
                          :error-messages="disableForm.errors.get('code')"
                          pattern="[0-9]*"
                          minlength="6"
                          maxlength="6"
                          required>
            </v-text-field>
          </v-card-text>
          <v-card-actions class="px-4">
            <v-btn :disabled="disableForm.busy"
                   @click.prevent="cancelTFA">
              Cancel
            </v-btn>
            <v-btn type="submit"
                   color="primary"
                   :disabled="disableForm.busy">
              Disable
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </div>
    <template v-if="!busy && (!loginSecurity || !loginSecurity.is_enabled)">
      <v-card v-if="step === 1">
        <v-card-title>
          Setup Two-Factor Authentication
        </v-card-title>
        <v-card-text class="panel-body">
          <h3>
            Setup Overview
          </h3>
          <p>
            Two-Factor Authentication adds an extra layer of security to your account by requiring more
            than just your username and password to log in. It adds the additional step of providing a
            one-time passcode generated by a Two-Factor Authentication phone app.
          </p>
          <div class="text--strong">
            What you'll need
          </div>
          <ol style="padding-left: 15px;">
            <li>
              About one minute to complete setup
            </li>
            <li>
              Your phone
            </li>
            <li>
              A Two-Factor Authentication app for your phone
            </li>
          </ol>
        </v-card-text>
        <v-card-actions class="px-4">
          <v-btn @click.prevent="start"
                 :disabled="busy"
                 color="primary">
            START SETUP
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-form @submit.prevent="submitCode"
              v-if="step === 2">
        <v-card>
          <v-card-title>
            Setup Two-Factor Authentication
          </v-card-title>
          <v-card-text>
            <h3>
              Two-Factor Phone App Setup
            </h3>
            <p>
              Scan the image below using the Two-Factor Authentication app on your phone. If you can't scan an image, <a href="#" @click.prevent="showSecretKey = !showSecretKey">enter this code into your app</a> instead.
            </p>
            <div class="MFA__QR-Container d-flex justify-center">
              <img :src="mfa.qr"
                   v-if="mfa && mfa.qr && !showSecretKey"
                   alt="QR Code"
                   class="MFA__QR-Code"/>

              <code v-if="showSecretKey"
                    class="MFA__QR-SecretKey">
                {{ mfa.key }}
              </code>
            </div>
            <h3>
              Enter the six-digit code from the app
            </h3>
            <p>
              After scanning the bar code image, your app will start generating unique six-digit passcodes which will change every 30 seconds. To confirm you have set this up correctly, please enter this code below and verify before proceeding.
            </p>
            <v-text-field v-model="form.code"
                          label="Enter your six-digit two-factor code"
                          outlined
                          autocomplete="one-time-code"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          placeholder="123456"
                          minlength="6"
                          maxlength="6"
                          required
                          :rules="[rules.required]"
                          :error-messages="form.errors.get('code')">
            </v-text-field>

          </v-card-text>
          <v-card-actions>
            <v-btn type="submit"
                   v-if="!configured"
                   :disabled="form.busy"
                   color="primary">
              Verify
            </v-btn>
            <v-btn v-if="loginSecurity && configured"
                   @click.prevent="next"
                   color="primary">
              Continue &rarr;
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>

      <v-card v-if="step === 3 && loginSecurity && loginSecurity.recovery_codes">
        <v-card-title class="panel-heading">
          Setup Two-Factor Authentication
        </v-card-title>
        <v-card-text>
          <h3>
            Backup Recovery Codes
          </h3>
          <p>
            Recovery codes can be used as a last resort to log into your account if you don't have access to the phone app you used to set up Two-Factor Authentication. Each of these codes can only be used once, and then they will no longer be valid.
          </p>

          <div class="alert alert-warning MFA__RecoveryCodes-Warning">
            Important: It's very difficult to verify your account if you lose both your phone and these codes. Please print them or save them somewhere so you will always be able to access them.
          </div>
          <div class="MFA__RecoveryCodes-Container"
               :class="{'Show': showRecoveryCodes}">
            <code>{{ loginSecurity.recovery_codes.join(' ') }}</code>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="showRecoveryCodes"
                 @click.prevent="copyRecoveryCodes">
            <v-icon left>
              fal fa-fw fa-clipboard
            </v-icon>
            <span v-if="!recoveryCodesCopied">
                Copy to Clipboard
              </span>
            <span v-if="recoveryCodesCopied">
                Copied!
              </span>
          </v-btn>
          <v-btn v-if="showRecoveryCodes || recoveryCodesCopied"
                 color="primary"
                 @click.prevent="next">
            Continue &rarr;
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="loginSecurity && configured && step === 4">
        <v-card-title>
          Setup Two-Factor Authentication
        </v-card-title>
        <v-card-text>
          <h3>
            Would you like to enable two-factor authentication now?
          </h3>
          <p>
            Once enabled, you'll be required to provide a six-digit passcode every time you login, which will be generated by the two-factor authentication app on your phone. Once logged in, you can disable Two-Factor Authentication at any time. Note you will be required to enter your account password when disabling Two-Factor Authentication.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn @click.prevent="cancelTFA">
            Cancel
          </v-btn>
          <v-btn @click.prevent="enableTFA"
                 color="primary">
            Enable
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import mfaService from '../../../../../ml/services/mfaService';
import clipboardService from '../../../../../ml/services/clipboardService';
import formValidation from '../../../plugins/formValidation';

export default {
  mixins: [formValidation],
  data() {
    return {
      busy: false,
      configured: false,
      recoveryForm: new Form({
        code: null,
      }),
      disableForm: new Form({
        code: null,
      }),
      form: new Form({
        code: null,
        enabled: false,
      }),
      loginSecurity: null,
      mfa: null,
      recoveryCodesCopied: false,
      showRecoveryCodes: true,
      showSecretKey: false,
      step: 1,
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    isEnabled() {
      return (this.loginSecurity && this.loginSecurity.google2fa_enabled);
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.busy = true;
      await this.getLoginSecurity();
      this.busy = false;
    },
    async start() {
      this.busy = true;
      await this.generateQRCode();
      this.next();
      this.busy = false;
    },
    async generateQRCode() {
      this.mfa = await mfaService.generate();
    },
    async getLoginSecurity() {
      return mfaService.getLoginSecurity()
        .then(data => {
          this.loginSecurity = data;
          if (this.loginSecurity) {
            this.configured = true;
          } else {
            this.configured = false;
          }
        })
        .catch(err => {
          this.configured = false;
          this.loginSecurity = null;
        })
    },
    previous() {
      this.goto(this.step - 1);
    },
    next() {
      this.goto(this.step + 1);
    },
    goto(step) {
      if (this.step < 1) {
        return;
      }
      this.step = step;
    },
    async copyRecoveryCodes() {
      await clipboardService.copy(this.loginSecurity.recovery_codes);
      this.recoveryCodesCopied = true;
    },
    submitCode() {
      if (this.form.busy) {
        return;
      }
      this.configured = false;
      this.form.startProcessing();
      mfaService.configure(this.mfa.key, this.form.code)
        .then(data => {
          this.loginSecurity = data;
          this.configured = true;
          this.form.code = null;
          this.next();
          this.form.finishProcessing();
        })
        .catch(err => {
          this.form.code = null;
          this.form.setErrors(err.response.data);
        })
        .finally(() => {
          this.form.busy = false;
        })
    },
    enableTFA() {
      mfaService.enable()
        .then(data => {
          this.loginSecurity = data;
          this.$store.commit('setSuccessMessage', 'Two-Factor Authentication is enabled.');
          this.$emit('enabled');
        })
    },
    async disableTFA() {
      if (this.disableForm.busy) {
        return;
      }
      this.disableForm.startProcessing();
      mfaService.disable(this.disableForm.code)
        .then(data => {
          return this.getLoginSecurity();
        })
        .then(data => {
          this.$store.commit('setSuccessMessage', 'Two-Factor Authentication is now disabled for your account.');
          this.disableForm.finishProcessing();
          this.$emit('disabled');
        })
        .catch(err => {
          this.disableForm.setErrors(err.response.data);
        })
        .finally(() => {
          this.disableForm.busy = false;
        })
    },
    cancelTFA() {
      //this.loginSecurity = null;
      this.configured = false;
      this.mfa = null;
      this.recoveryCodesCopied = false;
      this.showRecoveryCodes = false;
      this.showSecretKey = false;
      this.step = 1;
      this.$emit('cancelled');
    },
  }
}
</script>
